import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPrintJobStatus } from '../services/printService/luluApi';

export default function PrintStatusPage() {
  const { printJobId } = useParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Fetch status once when component mounts
    const fetchStatus = async () => {
      try {
        const result = await getPrintJobStatus(printJobId);
        setStatus(result);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };

    fetchStatus();
    // No polling - only fetch once
  }, [printJobId]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading print job status...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-red-800 text-lg font-medium">Error</h2>
          <p className="mt-2 text-red-700">{error}</p>
          <Link
            to="/library"
            className="mt-4 inline-block text-red-600 hover:text-red-800"
          >
            ← Back to library
          </Link>
        </div>
      </div>
    );
  }

  // Safely extract data from the response with multiple format support
  let statusText = 'UNKNOWN';
  let messageText = 'No status message available';
  let isUnpaid = false;
  
  // Handle status - could be direct string or nested object
  if (status) {
    if (typeof status.status === 'string') {
      // Handle old format: status.status = "CREATED"
      statusText = status.status;
      isUnpaid = statusText === 'CREATED';
    } else if (status.status && typeof status.status === 'object' && status.status.name) {
      // Handle new format: status.status.name = "UNPAID"
      statusText = status.status.name;
      isUnpaid = statusText === 'UNPAID';
    }
    
    // Handle message - could be direct string or part of nested status object
    if (typeof status.message === 'string') {
      messageText = status.message;
    } else if (status.status && typeof status.status === 'object' && status.status.message) {
      messageText = status.status.message;
    }
  }
  
  const paymentStatus = isUnpaid ? 'Unpaid' : 'Paid';
  
  // Extract other data safely
  const changed = status && status.changed ? status.changed : 
                 (status && status.status && status.status.changed ? status.status.changed : null);
  const lineItems = status && status.data && Array.isArray(status.data.line_items) ? status.data.line_items : [];
  const firstItem = lineItems.length > 0 ? lineItems[0] : {};
  const bookTitle = firstItem && typeof firstItem.title === 'string' ? firstItem.title : 'Unknown Title';
  const quantity = firstItem && typeof firstItem.quantity === 'number' ? firstItem.quantity : 1;
  const trackingId = firstItem && typeof firstItem.tracking_id === 'string' ? firstItem.tracking_id : null;
  const trackingUrl = firstItem && typeof firstItem.tracking_url === 'string' ? firstItem.tracking_url : null;

  // Format the timestamp if available
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString();
    } catch (e) {
      return '';
    }
  };
  
  const lastUpdated = formatDate(changed);

  // Function to manually refresh status
  const handleRefresh = async () => {
    setLoading(true);
    try {
      const result = await getPrintJobStatus(printJobId);
      setStatus(result);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">
              Print Job Status
            </h2>
            <button
              onClick={handleRefresh}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>

          <div className="space-y-6">
            {/* Status and Payment Badges */}
            <div className="flex items-center space-x-2">
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                ${statusText === 'CREATED' || statusText === 'UNPAID' ? 'bg-blue-100 text-blue-800' :
                  statusText === 'IN_PRODUCTION' ? 'bg-yellow-100 text-yellow-800' :
                  statusText === 'SHIPPED' ? 'bg-green-100 text-green-800' :
                  'bg-gray-100 text-gray-800'}`}>
                {statusText}
              </span>
              
              <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium
                ${isUnpaid ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                {paymentStatus}
              </span>
            </div>

            {/* Status Message */}
            <div className="bg-gray-50 rounded-md p-3">
              <p className="text-gray-700">{messageText}</p>
              {lastUpdated && (
                <p className="text-gray-500 text-sm mt-1">Last updated: {lastUpdated}</p>
              )}
            </div>
            
            {/* Payment Banner if unpaid */}
            {isUnpaid && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      This print job requires payment before it can be processed. Please visit the Lulu website to complete your payment.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Order Details */}
            <div className="border-t border-gray-200 pt-4">
              <dl className="divide-y divide-gray-200">
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Order ID</dt>
                  <dd className="text-sm text-gray-900">{printJobId}</dd>
                </div>
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Book Title</dt>
                  <dd className="text-sm text-gray-900">{bookTitle}</dd>
                </div>
                <div className="py-3 flex justify-between">
                  <dt className="text-sm font-medium text-gray-500">Quantity</dt>
                  <dd className="text-sm text-gray-900">{quantity}</dd>
                </div>
                {trackingId && trackingUrl && (
                  <div className="py-3 flex justify-between">
                    <dt className="text-sm font-medium text-gray-500">
                      Tracking Number
                    </dt>
                    <dd className="text-sm text-blue-600">
                      <a
                        href={trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {trackingId}
                      </a>
                    </dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Debug Info - to help diagnose the object structure */}
            <div className="mt-4 p-4 bg-gray-50 rounded-md text-xs">
              <details>
                <summary className="cursor-pointer font-medium">Debug Info</summary>
                <pre className="mt-2 overflow-auto max-h-96">
                  {JSON.stringify(status, null, 2)}
                </pre>
              </details>
            </div>

            {/* Back to Library */}
            <div className="mt-6">
              <Link
                to="/library"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Return to Library
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
